import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import {
  activateExternalProvider,
  createExternalProvider,
  deactivateExternalProvider,
  getExternalProviders,
  runExternalProviderHealthCheck,
  sendExternalProviderTestEmail,
  updateExternalProvider,
} from '../api/providersApi';
import { colorTokens, radiusTokens, typographyTokens } from '../app/designTokens';
import { useAuth } from '../auth/AuthContext';
import { hasPermission } from '../auth/permissions';
import { DataTable } from '../components/DataTable';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { StatusBadge } from '../components/StatusBadge';
import { TableToolbar } from '../components/TableToolbar';
import { SesHealthDetailsPanel, healthAlertSeverity } from '../components/SesHealthDetailsPanel';
import { Button } from '../components/ui/Button';
import { Select, SelectOption } from '../components/ui/Select';
import { TextInput } from '../components/ui/TextInput';
import { translateProviderHealthMessage } from '../utils/providerHealthMessages';

const { semantic } = colorTokens;

const PROVIDER_TYPES = ['storage', 'email', 'sms', 'push'];

const PROVIDER_KEYS = {
  storage: ['local', 's3'],
  email: ['local', 'ses'],
  sms: ['disabled', 'bulkgate'],
  push: ['disabled', 'local', 'pusher', 'reverb'],
};

const emptyForm = {
  provider_type: 'email',
  provider_key: 'ses',
  name: '',
  environment: 'staging',
  config_json: '{}',
  secrets_json: '{}',
};

const rowActionSx = {
  minHeight: 32,
  px: 1,
};

const deactivateOutlineSx = {
  ...rowActionSx,
  color: semantic.error,
  border: `1px solid ${semantic.error}`,
  bgcolor: 'transparent',
  '&:hover': {
    bgcolor: 'rgba(211, 47, 47, 0.08)',
    borderColor: semantic.error,
    color: semantic.error,
  },
};

const monoSx = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  fontSize: 12,
  lineHeight: '18px',
  color: semantic.text.primary,
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
};

const dialogPaperSx = {
  width: 'min(760px, calc(100vw - 48px))',
  maxWidth: 'min(760px, calc(100vw - 48px))',
  maxHeight: 'calc(100vh - 64px)',
  bgcolor: semantic.bg.surface,
  backgroundImage: 'none',
  border: `1px solid ${semantic.border.subtle}`,
  borderRadius: `${radiusTokens.card}px`,
};

function resolveSesTestRecipient(provider) {
  return provider?.resolved_config?.test_recipient
    || provider?.config?.test_recipient
    || '';
}

function translateTestEmailMessage(t, result) {
  const key = result?.message_key;
  if (key) {
    return t(`providers:${key}`, {
      defaultValue: result?.message || key,
    });
  }
  return result?.message || '';
}

function YesNoBadge({ value, t }) {
  return (
    <StatusBadge
      status={value ? 'yes' : 'no'}
      category="generic"
      apiLabel={value ? t('common:yes') : t('common:no')}
      size="Sm"
      tone={value ? 'Success' : 'Neutral'}
    />
  );
}

function providerStatusTone(status) {
  if (status === 'active' || status === 'ok') return 'Success';
  if (status === 'draft' || status === 'configured') return 'Warning';
  if (status === 'failed' || status === 'error' || status === 'inactive') return 'Danger';
  return 'Neutral';
}

function healthTone(status) {
  if (status === 'ok' || status === 'healthy' || status === 'success') return 'Success';
  if (status === 'skipped') return 'Warning';
  if (status === 'failed' || status === 'error') return 'Danger';
  return 'Neutral';
}

export function ExternalProvidersPage() {
  const { t } = useTranslation(['providers', 'common']);
  const { session } = useAuth();
  const canManage = hasPermission(session, 'system.providers.manage');
  const canHealthCheck = hasPermission(session, 'system.providers.health_check');
  const canSendTestEmail = canManage || canHealthCheck;

  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [healthResult, setHealthResult] = useState(null);
  const [testEmailResult, setTestEmailResult] = useState(null);
  const [testEmailConfirm, setTestEmailConfirm] = useState(null);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  const emptyValue = t('common:empty_value');
  const recordCount = providers.length;

  const loadProviders = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getExternalProviders({ per_page: 100 });
      setProviders(response.data || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = useCallback((provider) => {
    setEditing(provider);
    const config = { ...(provider.config || {}) };
    if (provider.provider_key === 'ses' && provider.resolved_config) {
      const resolvedFrom = provider.resolved_config.from_address;
      const currentFrom = config.from_address;
      const isPlaceholder = typeof currentFrom === 'string'
        && (currentFrom.toLowerCase() === 'system@mafert.local' || String(currentFrom).toLowerCase().endsWith('.local'));
      if ((!currentFrom || isPlaceholder) && resolvedFrom) {
        config.from_address = resolvedFrom;
      }
      if (!config.identity_domain && provider.resolved_config.identity_domain) {
        config.identity_domain = provider.resolved_config.identity_domain;
      }
      if (!config.test_recipient && provider.resolved_config.test_recipient) {
        config.test_recipient = provider.resolved_config.test_recipient;
      }
      if (!config.region && provider.resolved_config.region) {
        config.region = provider.resolved_config.region;
      }
    }
    setForm({
      provider_type: provider.provider_type,
      provider_key: provider.provider_key,
      name: provider.name,
      environment: provider.environment || 'local',
      config_json: JSON.stringify(config, null, 2),
      secrets_json: '{}',
    });
    setDialogOpen(true);
  }, []);

  const parseJsonField = (value, fieldName) => {
    try {
      return JSON.parse(value || '{}');
    } catch {
      throw new Error(t('providers:list.invalid_json', { field: fieldName }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');

    try {
      const payload = {
        provider_type: form.provider_type,
        provider_key: form.provider_key,
        name: form.name,
        environment: form.environment,
        config: parseJsonField(form.config_json, 'config'),
      };

      const secrets = parseJsonField(form.secrets_json, 'secrets');
      if (Object.keys(secrets).length > 0) {
        payload.secrets = secrets;
      }

      if (editing) {
        await updateExternalProvider(editing.id, payload);
      } else {
        await createExternalProvider(payload);
      }

      setDialogOpen(false);
      setMessage(t('providers:list.saved'));
      await loadProviders();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = useCallback(async (provider) => {
    if (provider.is_active) {
      await deactivateExternalProvider(provider.id);
    } else {
      await activateExternalProvider(provider.id);
    }

    await loadProviders();
  }, [loadProviders]);

  const handleHealthCheck = useCallback(async (provider) => {
    setHealthResult(null);
    setTestEmailResult(null);
    try {
      const result = await runExternalProviderHealthCheck(provider.id);
      const translated = translateProviderHealthMessage(t, result, result.message);
      setMessage(t('providers:list.health_check_result', { status: result.status, message: translated }));
      setHealthResult({
        ...result,
        provider_key: provider.provider_key,
        details: result.health_check?.details || result.details || {},
      });
      await loadProviders();
    } catch (err) {
      const payload = err?.response?.data?.data || err?.response?.data || {};
      const translated = translateProviderHealthMessage(t, payload, payload.message);
      setMessage(t('providers:list.health_check_result', {
        status: payload.status || 'failed',
        message: translated || t('providers:health.messages.healthCheckFailed'),
      }));
      setHealthResult({
        ...payload,
        provider_key: provider.provider_key,
        details: payload.health_check?.details || payload.details || {},
      });
    }
  }, [loadProviders, t]);

  const openTestEmailConfirm = useCallback((provider) => {
    const recipient = resolveSesTestRecipient(provider);
    setTestEmailConfirm({ provider, recipient });
  }, []);

  const handleSendTestEmail = async () => {
    if (!testEmailConfirm?.provider) {
      return;
    }

    const { provider } = testEmailConfirm;
    setSendingTestEmail(true);
    setHealthResult(null);
    setMessage('');

    try {
      const result = await sendExternalProviderTestEmail(provider.id);
      setTestEmailResult(result);
      setMessage(translateTestEmailMessage(t, result));
      setTestEmailConfirm(null);
      await loadProviders();
    } catch (err) {
      const payload = err?.response?.data?.data || err?.response?.data || {};
      const result = {
        status: payload.status || 'failed',
        message: payload.message,
        message_key: payload.message_key || 'email.testEmailFailed',
        details: payload.details || {},
      };
      setTestEmailResult(result);
      setMessage(translateTestEmailMessage(t, result));
      setTestEmailConfirm(null);
    } finally {
      setSendingTestEmail(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        id: 'name',
        label: t('common:name'),
        render: (row) => row.name || emptyValue,
      },
      {
        id: 'provider_type',
        label: t('common:type'),
        render: (row) => (
          <Typography sx={monoSx}>{row.provider_type || emptyValue}</Typography>
        ),
      },
      {
        id: 'provider_key',
        label: t('common:key'),
        render: (row) => (
          <Typography sx={monoSx}>{row.provider_key || emptyValue}</Typography>
        ),
      },
      {
        id: 'environment',
        label: t('common:environment'),
        render: (row) => (
          <Typography sx={monoSx}>{row.environment || emptyValue}</Typography>
        ),
      },
      {
        id: 'is_active',
        label: t('common:active_label'),
        render: (row) => <YesNoBadge value={Boolean(row.is_active)} t={t} />,
      },
      {
        id: 'is_default',
        label: t('providers:list.default'),
        render: (row) => <YesNoBadge value={Boolean(row.is_default)} t={t} />,
      },
      {
        id: 'status',
        label: t('common:status'),
        render: (row) => (
          row.status
            ? (
              <StatusBadge
                status={row.status}
                category="generic"
                apiLabel={row.status}
                size="Sm"
                tone={providerStatusTone(row.status)}
              />
            )
            : emptyValue
        ),
      },
      {
        id: 'last_health_status',
        label: t('common:health'),
        render: (row) => (
          row.last_health_status
            ? (
              <StatusBadge
                status={row.last_health_status}
                category="generic"
                apiLabel={row.last_health_status}
                size="Sm"
                tone={healthTone(row.last_health_status)}
              />
            )
            : emptyValue
        ),
      },
      {
        id: 'actions',
        label: t('common:actions'),
        align: 'right',
        render: (row) => {
          const isSes = row.provider_type === 'email' && row.provider_key === 'ses';
          const recipient = resolveSesTestRecipient(row);
          const missingRecipient = isSes && !recipient;
          const sandbox = row.resolved_config?.sandbox !== false;

          return (
            <Stack
              direction="row"
              spacing={0.75}
              flexWrap="wrap"
              useFlexGap
              justifyContent="flex-end"
              alignItems="center"
            >
              {canManage && (
                <Button variant="Ghost" onClick={() => openEdit(row)} sx={rowActionSx}>
                  {t('common:edit')}
                </Button>
              )}
              {canManage && (
                row.is_active ? (
                  <Button variant="Ghost" onClick={() => handleToggleActive(row)} sx={deactivateOutlineSx}>
                    {t('common:deactivate')}
                  </Button>
                ) : (
                  <Button variant="Secondary" onClick={() => handleToggleActive(row)} sx={rowActionSx}>
                    {t('common:activate')}
                  </Button>
                )
              )}
              {canHealthCheck && (
                <Button variant="Ghost" onClick={() => handleHealthCheck(row)} sx={rowActionSx}>
                  {t('providers:list.health_check')}
                </Button>
              )}
              {canSendTestEmail && isSes && (
                <Button
                  variant="Secondary"
                  disabled={missingRecipient}
                  title={missingRecipient ? t('providers:email.missingTestRecipient') : undefined}
                  onClick={() => openTestEmailConfirm(row)}
                  sx={rowActionSx}
                >
                  {t('providers:actions.sendTestEmail')}
                </Button>
              )}
              {isSes && sandbox && missingRecipient && (
                <Typography
                  sx={{
                    fontSize: 11,
                    color: semantic.warning,
                    alignSelf: 'center',
                  }}
                >
                  {t('providers:email.missingTestRecipient')}
                </Typography>
              )}
            </Stack>
          );
        },
      },
    ],
    [canHealthCheck, canManage, canSendTestEmail, emptyValue, handleHealthCheck, handleToggleActive, openEdit, openTestEmailConfirm, t],
  );

  const secretsHelper = editing?.has_secrets
    ? t('providers:list.secrets_helper_edit', {
      keys: (editing.secret_keys || []).join(', ') || t('providers:list.secrets_helper_no_keys'),
    })
    : t('providers:list.secrets_helper_new');

  const sesResolved = editing?.provider_key === 'ses' ? editing.resolved_config : null;
  const sesFromLooksLocal = typeof form.config_json === 'string'
    && /system@mafert\.local|@[a-z0-9.-]+\.local"/i.test(form.config_json);

  const testEmailDetails = testEmailResult?.details || {};

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="List"
        title={t('providers:list.title')}
        subtitle={t('providers:list.subtitle')}
        actions={canManage ? (
          <Button variant="Accent" onClick={openCreate}>
            {t('providers:list.new_provider')}
          </Button>
        ) : null}
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        {t('providers:list.foundation_alert')}
      </Alert>

      {message && !testEmailResult && (
        <Alert severity={healthAlertSeverity(healthResult?.status)} sx={{ mb: 2 }}>
          {message}
          {healthResult?.provider_key === 'ses' && (
            <SesHealthDetailsPanel details={healthResult.details} />
          )}
        </Alert>
      )}

      {testEmailResult && (
        <Alert
          severity={testEmailResult.status === 'ok' ? 'success' : 'error'}
          sx={{ mb: 2 }}
          onClose={() => setTestEmailResult(null)}
        >
          {message || translateTestEmailMessage(t, testEmailResult)}
          <Box sx={{ mt: 1 }}>
            {testEmailDetails.sandbox && (
              <Typography sx={{ fontSize: 13, mb: 0.5, color: semantic.text.secondary }}>
                {t('providers:email.sandboxNotice')}
              </Typography>
            )}
            <Typography sx={{ fontSize: 13, color: semantic.text.secondary }}>
              {t('providers:labels.fromAddress')}:{' '}
              <Box component="span" sx={monoSx}>
                {testEmailDetails.from_address || emptyValue}
              </Box>
            </Typography>
            <Typography sx={{ fontSize: 13, color: semantic.text.secondary }}>
              {t('providers:labels.recipient')}:{' '}
              <Box component="span" sx={monoSx}>
                {testEmailDetails.recipient || emptyValue}
              </Box>
            </Typography>
            <Typography sx={{ fontSize: 13, color: semantic.text.secondary }}>
              {t('providers:labels.region')}:{' '}
              <Box component="span" sx={monoSx}>
                {testEmailDetails.region || emptyValue}
              </Box>
            </Typography>
            {testEmailDetails.message_id && (
              <Typography sx={{ fontSize: 13, color: semantic.text.secondary }}>
                {t('providers:labels.messageId')}:{' '}
                <Box component="span" sx={monoSx}>{testEmailDetails.message_id}</Box>
              </Typography>
            )}
            {testEmailDetails.sent_at && (
              <Typography sx={{ fontSize: 13, color: semantic.text.secondary }}>
                {t('providers:labels.sentAt')}:{' '}
                <Box component="span" sx={monoSx}>{testEmailDetails.sent_at}</Box>
              </Typography>
            )}
            {testEmailDetails.failed_step && (
              <Typography sx={{ fontSize: 13, color: semantic.text.secondary }}>
                {t('providers:labels.failedStep')}:{' '}
                <Box component="span" sx={monoSx}>{testEmailDetails.failed_step}</Box>
              </Typography>
            )}
            {testEmailDetails.aws_error_code && (
              <Typography sx={{ fontSize: 13, color: semantic.text.secondary }}>
                {t('providers:labels.awsErrorCode')}:{' '}
                <Box component="span" sx={monoSx}>{testEmailDetails.aws_error_code}</Box>
              </Typography>
            )}
            {testEmailDetails.safe_error_message && (
              <Typography sx={{ fontSize: 13, color: semantic.text.secondary }}>
                {testEmailDetails.safe_error_message}
              </Typography>
            )}
            {testEmailDetails.suggested_iam_action && (
              <Typography sx={{ fontSize: 13, color: semantic.text.secondary }}>
                {t('providers:health.ses.suggested_iam_action')}:{' '}
                <Box component="span" sx={monoSx}>{testEmailDetails.suggested_iam_action}</Box>
              </Typography>
            )}
          </Box>
        </Alert>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={(
            <Button variant="Ghost" onClick={loadProviders} sx={{ minHeight: 32 }}>
              {t('common:retry')}
            </Button>
          )}
        >
          {t('common:load_failed')}
        </Alert>
      )}

      <SectionCard variant="Default" noPadding sx={{ width: '100%' }}>
        <TableToolbar
          mode="Default"
          title={t('providers:list.configurations')}
          meta={(
            <StatusBadge
              status="list"
              category="generic"
              apiLabel={t('providers:list.toolbar_count', { count: recordCount })}
              size="Sm"
              tone="Neutral"
            />
          )}
        />

        {loading ? (
          <LoadingSkeleton
            type="Table"
            embedded
            rows={3}
            columns={columns.length}
          />
        ) : (
          <DataTable
            type="Default"
            embedded
            columns={columns}
            rows={providers}
            getRowKey={(row) => row.id}
            emptyMessage={t('common:no_data')}
            sx={{
              overflowX: 'auto',
              width: '100%',
              '& table': {
                width: '100%',
                minWidth: '100%',
              },
            }}
          />
        )}
      </SectionCard>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth={false}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            fontFamily: typographyTokens.fontFamily,
            fontWeight: typographyTokens.fontWeightSemibold,
            fontSize: 16,
            color: semantic.text.primary,
            borderBottom: `1px solid ${semantic.border.subtle}`,
          }}
        >
          {editing ? t('providers:list.edit_provider') : t('providers:list.create_provider')}
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5, overflowY: 'auto' }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Select
              density="Compact"
              fullWidth
              label={t('providers:list.provider_type')}
              value={form.provider_type}
              disabled={Boolean(editing)}
              onChange={(e) => setForm((prev) => ({
                ...prev,
                provider_type: e.target.value,
                provider_key: PROVIDER_KEYS[e.target.value]?.[0] || '',
              }))}
            >
              {PROVIDER_TYPES.map((type) => (
                <SelectOption key={type} value={type}>{type}</SelectOption>
              ))}
            </Select>
            <Select
              density="Compact"
              fullWidth
              label={t('providers:list.provider_key')}
              value={form.provider_key}
              disabled={Boolean(editing)}
              onChange={(e) => setForm((prev) => ({ ...prev, provider_key: e.target.value }))}
            >
              {(PROVIDER_KEYS[form.provider_type] || []).map((key) => (
                <SelectOption key={key} value={key}>{key}</SelectOption>
              ))}
            </Select>
            <TextInput
              density="Compact"
              label={t('common:name')}
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            />
            <TextInput
              density="Compact"
              label={t('common:environment')}
              value={form.environment}
              onChange={(e) => setForm((prev) => ({ ...prev, environment: e.target.value }))}
            />
            <TextInput
              density="Compact"
              label={t('providers:list.config_json')}
              value={form.config_json}
              onChange={(e) => setForm((prev) => ({ ...prev, config_json: e.target.value }))}
              multiline
              minRows={4}
            />
            {form.provider_key === 'ses' && sesResolved && (
              <Alert severity="info">
                {t('providers:health.ses.from_address')}: {sesResolved.from_address || emptyValue}
                {' · '}
                {t('providers:health.ses.from_source')}: {sesResolved.source_map?.from_address || emptyValue}
              </Alert>
            )}
            {form.provider_key === 'ses' && (sesFromLooksLocal || sesResolved?.is_local_placeholder_from) && (
              <Alert severity="warning">
                {t('providers:health.ses.local_placeholder_warning')}
              </Alert>
            )}
            <TextInput
              density="Compact"
              label={t('providers:list.secrets_json')}
              type="password"
              value={form.secrets_json}
              onChange={(e) => setForm((prev) => ({ ...prev, secrets_json: e.target.value }))}
              multiline
              minRows={3}
              helperText={secretsHelper}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="Ghost" onClick={() => setDialogOpen(false)}>
            {t('common:cancel')}
          </Button>
          <Button
            variant="Accent"
            onClick={handleSave}
            disabled={saving}
            loading={saving}
          >
            {t('common:save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(testEmailConfirm)}
        onClose={() => !sendingTestEmail && setTestEmailConfirm(null)}
        maxWidth={false}
        PaperProps={{
          sx: {
            ...dialogPaperSx,
            width: 'min(480px, calc(100vw - 48px))',
            maxWidth: 'min(480px, calc(100vw - 48px))',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: typographyTokens.fontFamily,
            fontWeight: typographyTokens.fontWeightSemibold,
            fontSize: 16,
            color: semantic.text.primary,
          }}
        >
          {t('providers:actions.sendTestEmail')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            {(testEmailConfirm?.provider?.resolved_config?.sandbox !== false) && (
              <Alert severity="info">{t('providers:email.sandboxNotice')}</Alert>
            )}
            <Typography sx={{ fontSize: 13, color: semantic.text.primary }}>
              {t('providers:confirmations.sendTestEmail', {
                recipient: testEmailConfirm?.recipient || emptyValue,
              })}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            variant="Ghost"
            onClick={() => setTestEmailConfirm(null)}
            disabled={sendingTestEmail}
          >
            {t('common:cancel')}
          </Button>
          <Button
            variant="Accent"
            onClick={handleSendTestEmail}
            disabled={sendingTestEmail || !testEmailConfirm?.recipient}
            loading={sendingTestEmail}
          >
            {t('providers:actions.sendTestEmail')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
